import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { bounds, placePanel } from './geometry';
import { useInspectHost } from './context';

/**
 * Positions a floating panel against a selected target: below it when it fits, above it
 * otherwise, scrolling within the available space without covering it. `sheet` (and every
 * phone) renders a bottom sheet instead. When the target is scrolled out of view the panel waits, hidden, rather
 * than floating somewhere unrelated.
 */
export function AnchoredToTarget({
  targetId,
  placement = 'auto',
  children,
}: {
  targetId: string;
  placement?: 'auto' | 'below' | 'sheet';
  children: ReactNode;
}) {
  const host = useInspectHost();
  const panel = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 320, height: 200 });
  const rects = host?.rectsFor(targetId) ?? [];
  const target = bounds(rects);
  useLayoutEffect(() => {
    const r = panel.current?.getBoundingClientRect();
    if (r && (Math.abs(r.width - size.width) > 1 || Math.abs(r.height - size.height) > 1))
      setSize({ width: r.width, height: r.height });
  });
  if (!host) return <>{children}</>;
  if (placement === 'sheet' || host.phone) {
    return (
      <div
        className="pc-inspect-sheet"
        role="dialog"
        aria-modal="false"
        data-testid="inspect-anchor"
        data-placement="sheet"
      >
        {children}
      </div>
    );
  }
  // Scrolled out of view: the panel stays mounted (an edit in flight keeps its state) but
  // hidden, rather than floating somewhere unrelated.
  if (!target)
    return (
      <div
        className="pc-inspect-anchor"
        data-testid="inspect-anchor"
        data-placement="offscreen"
        hidden
      >
        {children}
      </div>
    );
  const spot = placePanel(target, size, host.viewport);
  const where = placement === 'below' ? 'below' : spot.placement;
  return (
    <div
      ref={panel}
      className="pc-inspect-anchor"
      data-testid="inspect-anchor"
      data-placement={where}
      style={{ transform: `translate(${spot.x}px, ${spot.y}px)`, maxHeight: spot.maxHeight }}
    >
      {children}
    </div>
  );
}
